import CaseStudy from '../components/CaseStudy';

const data = {
  company: 'Propel Search · 2024',
  title: 'From Job Search<br/>to Networking<br/>Platform',
  subtitle: 'A full product pivot - redesigning how job seekers find opportunities by making LinkedIn networking effortless through AI.',
  metaRole: 'Solo Product Designer',
  metaPlatform: 'Chrome Extension + Web App',
  metaExtraLabel: 'Scope',
  metaExtra: '0 → 1 · Strategy + Execution',
  outcomeValue: '$1K MRR',
  outcomeSub: 'within first few months of launch',
  heroPlaceholder: 'Hero image: Full-width screenshot of the Propel Chrome extension open on a LinkedIn profile - showing the AI sidebar with insights and outreach tools',
  heroImage: '/propel-hero-img.png',
  metaItems: [
    { label: 'My Role', value: 'Solo Product Designer' },
    { label: 'Platform', value: 'Chrome Extension + Web App' },
    { label: 'Scope', value: '0 → 1 · Strategy + Execution' },
    { label: 'Outcome', value: '$1K MRR within first few months' },
  ],
  sections: [
    {
      type: 'text',
      label: 'Overview',
      heading: 'A crowded market needed a different angle.',
      paragraphs: [
        'Propel Search started as a job search, tracker, and interview prep platform - a space already dominated by large, well-funded competitors. The founder recognised that simply doing what others did, but slightly better, wasn\'t a path to growth.',
        'The insight: <strong>most people don\'t get jobs through job boards. They get them through people.</strong> Networking was the real unlock - but it was painful, awkward, and time-consuming. That became our design challenge.',
      ],
    },
    {
      type: 'text',
      label: 'Problem',
      heading: 'Job seekers know networking matters. They just don\'t know how.',
      paragraphs: [
        'When we spoke to job seekers, the same frustrations surfaced: finding the right people felt like guesswork, crafting personalised messages from scratch was exhausting, and following up without feeling intrusive was hard to get right.',
        'Existing tools either replicated the job board experience or offered generic CRM-style trackers. Nobody was solving the human, relational side of job searching.',
      ],
      hmw: 'How might we make meaningful professional networking as easy as a single click - without making it feel robotic?',
    },
    {
      type: 'text',
      label: 'Solution',
      heading: 'A LinkedIn-native networking co-pilot, powered by AI.',
      paragraphs: [
        'Rather than pulling people away from where networking already happens, we built <strong>directly on top of LinkedIn</strong> as a Chrome extension. Zero friction - the tool appeared exactly where users needed it.',
        '<strong>Smart people search</strong> - surface the right contacts based on role, company, and career trajectory. <strong>Commonality finder</strong> - AI scans a profile and surfaces shared experiences that make cold outreach feel warm. <strong>Personalised outreach generation</strong> - one-click tailored LinkedIn DMs, connection notes, and emails with adjustable messaging levers. <strong>Follow-up reminder engine</strong> - so no connection falls through the cracks.',
      ],
    },
    {
      type: 'image',
      size: 'tall',
      src: '/insights-propel.png',
      text: 'Screenshot: Propel sidebar open on a LinkedIn profile - AI insights panel with commonalities and the outreach generator open',
      caption: 'The extension sidebar - AI insights and LinkedIn Embedding',
    },
    {
      type: 'image',
      size: 'medium',
      src: '/message-propel.png',
      text: 'Screenshot: The outreach message generator - showing tone/intent sliders and generated DM, email, and connection note variants',
      caption: 'Personalised outreach generation with adjustable messaging levers',
    },
    {
      type: 'image',
      size: 'medium',
      src: '/reminder-propel.png',
      text: 'Screenshot: The follow-up reminder engine - list of contacts with follow-up nudges and status tracking',
      caption: 'Follow-up reminder engine - making sure no relationship goes cold',
    },
    {
      type: 'text',
      label: 'Impact',
      heading: 'A validated pivot that paid off.',
      paragraphs: [],
      impact: [
        { num: '$1K', label: 'MRR within first few months of launch' },
        { num: '0→1', label: 'Full product pivot designed solo' },
        { num: 'MVP', label: 'Shipped as pilot LinkedIn extension' },
      ],
    },
    {
      type: 'text',
      label: 'Learnings',
      heading: 'What this project taught me.',
      paragraphs: [
        'Pivoting a product is as much a design challenge as a strategic one. The hardest part wasn\'t designing new screens - it was <strong>deciding what to kill</strong>. Every feature from the old product had a user who loved it. Ruthless clarity about what the new positioning demanded was everything.',
        'Building on top of LinkedIn forced constraints that turned out to be features. We couldn\'t change where people worked - so we had to meet them there. That shaped a product that felt native rather than disruptive.',
      ],
    },
  ],
  nextTo: '/onboarding',
  nextLabel: 'Next Project',
  nextTitle: 'When OTP Fails: Fixing Onboarding',
};

export default function Propel() {
  return <CaseStudy data={data} />;
}
